import { forwardRef, type HTMLAttributes, type ReactElement } from 'react';

import type { DataTestId } from '../../core/types/data-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import type { FlexOwnProps } from '../flex/Flex.js';

// aliased for backwards compatibility until the unused props are deprecated
/**
 * @public
 */
export interface ActionItemGroupProps
  extends FlexOwnProps,
    HTMLAttributes<HTMLElement>,
    WithChildren,
    StylingProps,
    DataTestId {
  /**
   * Whether or not the ActionGroup is collapsible.
   * @defaultValue true
   */
  collapsible?: boolean;
  /**
   * Whether the items in the ActionGroup should have a roving focus applied.
   * @defaultValue true
   */
  rovingFocus?: boolean;
  /**
   * Whether to collapse starting from the beginning or end of the ActionGroup.
   * @defaultValue start
   */
  collapseFrom?: 'start' | 'end';
  /**
   * Icon used for the trigger that opens/closes the overlay when the ActionGroup is collapsed.
   */
  triggerIcon?: ReactElement;
  /**
   * Influences the spacings. Condensed is more compact.
   */
  density?: 'default' | 'condensed';
  /**
   * Handler called when the collapsed state changes.
   */
  onCollapsedChange?: (isCollapsed: boolean) => void;
  /**
   * Handler called when at least one item is moved into the menu.
   */
  onOverflowChange?: (isCollapsed: boolean) => void;
}

/**
 * Groups `AppHeader.ActionItem` children and separates groups with a divider.
 * @public
 */
export const ActionItemGroup = forwardRef<HTMLDivElement, ActionItemGroupProps>(
  (props, ref) => <div ref={ref} {...props} />,
);

(
  ActionItemGroup as typeof ActionItemGroup & { displayName: string }
).displayName = 'ActionItemGroup';
